import React, { useState } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { TrainingSample } from '../types';
import { GET_DISEASES_SAFE, SEARCH_DISEASES } from '../graphql/operations';

// Custom hooks
import { useTrainingSamples } from '../hooks/useTrainingSamples';
import { useTrainingSampleDelete } from '../hooks/useTrainingSampleDelete';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useOptimizedSignedImages } from '../hooks/useOptimizedSignedImages';
import { useScrollPersistence } from '../hooks/useScrollPersistence';

// Modular components
import { TrainingSampleHeader } from './training/TrainingSampleHeader';
import { TrainingSampleFilters } from './training/TrainingSampleFilters';
import { TrainingSampleGrid } from './training/TrainingSampleGrid';
import { DeleteTrainingSampleDialog } from './training/DeleteTrainingSampleDialog';
import { TrainingSampleInferenceDialog } from './training/TrainingSampleInferenceDialog';
import { FullScreenImageViewer } from './FullScreenImageViewer';

const TrainingSampleList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSample, setSelectedSample] = useState<TrainingSample | null>(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState<number>(0);
  const [inferenceSample, setInferenceSample] = useState<TrainingSample | null>(null);
  const [inferenceDialogOpen, setInferenceDialogOpen] = useState(false);

  // Fetch diseases for the filter dropdown
  const { data: diseasesData, loading: diseasesLoading } = useQuery(GET_DISEASES_SAFE, {
    variables: { limit: 10 }
  });
  const [searchDiseases, { data: searchResults, loading: searchLoading }] = useLazyQuery(SEARCH_DISEASES);
  const diseases = searchResults?.searchDiseases?.items || diseasesData?.listDiseases?.items || [];

  const handleDiseaseSearch = (input: string) => {
    const trimmed = input.trim();
    if (trimmed.length === 0) {
      return;
    }

    searchDiseases({
      variables: {
        query: trimmed,
        limit: 20,
      },
    });
  };

  const handleDiseaseSelect = (disease: (typeof diseases)[number] | null) => {
    if (!disease) {
      return;
    }

    searchDiseases({
      variables: {
        query: disease.name,
        limit: 20,
      },
    });
  };

  // Custom hooks for business logic
  const trainingSamples = useTrainingSamples({ limit: 50 });
  const deleteHandler = useTrainingSampleDelete(() => trainingSamples.refetch());
  
  // Scroll position persistence
  useScrollPersistence();

  // Optimized signed images - only fetches new URLs
  const imageUrls = trainingSamples.samples.map((sample: TrainingSample) => 
    sample.thumbnail_url || sample.original_image_url
  );
  const signedImages = useOptimizedSignedImages(imageUrls);

  // Infinite scroll
  useInfiniteScroll({
    hasMore: trainingSamples.hasMore,
    loading: trainingSamples.loadingMore,
    onLoadMore: trainingSamples.loadMoreSamples,
  });

  const handleSampleClick = (sample: TrainingSample) => {
    if (isMobile) {
      // On mobile, navigate to detail view
      navigate(`/training-samples/${sample.id}`);
    } else {
      // On desktop, open full screen image viewer
      setSavedScrollPosition(window.scrollY);
      setSelectedSample(sample);
      setImageViewerOpen(true);
    }
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleRunInferenceClick = (sample: TrainingSample, event: React.MouseEvent) => {
    event.stopPropagation();
    setInferenceSample(sample);
    setInferenceDialogOpen(true);
  };

  if (trainingSamples.loading) return <CircularProgress />;
  if (trainingSamples.error) return <Alert severity="error">Error loading training samples: {trainingSamples.error.message}</Alert>;

  return (
    <Box>
      <TrainingSampleHeader 
        title="Training Samples"
        totalCount={trainingSamples.samples.length}
      />

      <TrainingSampleFilters
        filter={trainingSamples.filter}
        dateFilter={trainingSamples.dateFilter}
        showFilters={showFilters}
        diseases={diseases}
        diseaseLoading={diseasesLoading || searchLoading}
        sortDirection={trainingSamples.sortDirection}
        onFilterChange={trainingSamples.setFilter}
        onDateFilterChange={trainingSamples.handleDateFilterChange}
        onToggleFilters={handleToggleFilters}
        onApplyFilter={trainingSamples.handleApplyFilter}
        onClearFilters={trainingSamples.handleClearFilters}
        onDiseaseSearch={handleDiseaseSearch}
        onDiseaseSelect={handleDiseaseSelect}
        onSortDirectionChange={trainingSamples.handleSortDirectionChange}
      />

      <TrainingSampleGrid
        samples={trainingSamples.samples}
        signedImages={signedImages}
        loadingMore={trainingSamples.loadingMore}
        hasMore={trainingSamples.hasMore}
        onSampleClick={handleSampleClick}
        onDeleteClick={deleteHandler.handleDeleteClick}
        onRunInference={handleRunInferenceClick}
      />

      <DeleteTrainingSampleDialog
        open={deleteHandler.deleteDialogOpen}
        sample={deleteHandler.selectedSample}
        onClose={deleteHandler.handleDeleteCancel}
        onConfirm={deleteHandler.handleDeleteConfirm}
        isDeleting={deleteHandler.isDeleting}
      />

      <TrainingSampleInferenceDialog
        open={inferenceDialogOpen}
        sample={inferenceSample}
        imageUrl={
          inferenceSample
            ? (signedImages[inferenceSample.thumbnail_url || inferenceSample.original_image_url]?.signedUrl || undefined)
            : undefined
        }
        onClose={() => {
          setInferenceDialogOpen(false);
          setInferenceSample(null);
        }}
      />

      {/* Full Screen Image Viewer */}
      {selectedSample && (
        <FullScreenImageViewer
          open={imageViewerOpen}
          onClose={() => {
            setImageViewerOpen(false);
            setSelectedSample(null);
            // Restore scroll position after modal closes
            requestAnimationFrame(() => {
              window.scrollTo(0, savedScrollPosition);
            });
          }}
          imageUrl={signedImages[selectedSample.thumbnail_url || selectedSample.original_image_url]?.signedUrl || undefined}
          sample={selectedSample}
          onSampleUpdated={() => trainingSamples.refetch()}
        />
      )}
    </Box>
  );
};

export default TrainingSampleList;